// FM-02 — a baseline never witnessed by the paying party.
//
// The witnessing obligation changed between DOE ESPC IDIQ generations: Gen 2
// §C.4.6 says the agency *should* witness, Gen 3 and Gen 4 require it. So
// identical plan text carries a different obligation depending on award date —
// deterministic, checkable, and invisible to anyone reading the plan alone.
//
// The ordering periods overlap, because a generation stays orderable after its
// successor opens. Where a date falls in two, both are returned and the flag
// says so.

import type { Flag, SourceDocument } from '../../types';
import { evidenceFor, detectionSignal, tierOf } from '../../data/taxonomy';
import { buildLocator, sectionRefOf } from './locate';
import { enrichFlag } from '../finding';

export const CHECK_ID = 'generation-check';

export type Witnessing = 'should' | 'shall';

export interface Generation {
  generation: 1 | 2 | 3 | 4;
  label: string;
  /** Inclusive ISO bounds. `to` omitted means still open. */
  from: string;
  to?: string;
  witnessing: Witnessing;
  clause: string;
}

/** Ordering periods, which overlap by design. */
export const IDIQ_GENERATIONS: Generation[] = [
  {
    generation: 1,
    label: 'Gen 1',
    from: '1998-11-01',
    to: '2010-03-31',
    witnessing: 'should',
    clause: 'DOE ESPC IDIQ Gen 1',
  },
  {
    generation: 2,
    label: 'Gen 2',
    from: '2008-12-01',
    to: '2019-12-31',
    witnessing: 'should',
    clause: 'DOE ESPC IDIQ Gen 2 §C.4.6',
  },
  {
    generation: 3,
    label: 'Gen 3',
    from: '2017-04-01',
    to: '2026-04-30',
    witnessing: 'shall',
    clause: 'DOE ESPC IDIQ Gen 3',
  },
  {
    generation: 4,
    label: 'Gen 4',
    from: '2023-08-01',
    witnessing: 'shall',
    clause: 'DOE ESPC IDIQ Gen 4',
  },
];

export interface GenerationLookup {
  awardDate: string;
  /** Every generation orderable on that date. */
  matches: Generation[];
  /** The latest matching generation governs. */
  governing: Generation | null;
  overlapping: boolean;
  note: string;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function lookupGeneration(awardDate: string): GenerationLookup {
  if (!ISO_DATE.test(awardDate)) {
    return {
      awardDate,
      matches: [],
      governing: null,
      overlapping: false,
      note: `"${awardDate}" is not a YYYY-MM-DD date, so no generation can be looked up.`,
    };
  }

  const matches = IDIQ_GENERATIONS.filter(
    (g) => awardDate >= g.from && (!g.to || awardDate <= g.to),
  );

  if (matches.length === 0) {
    return {
      awardDate,
      matches,
      governing: null,
      overlapping: false,
      note: `${awardDate} falls outside every DOE ESPC IDIQ ordering period, so the witnessing obligation cannot be determined from the award date alone.`,
    };
  }

  // The most recent orderable generation governs.
  const governing = matches[matches.length - 1];
  const overlapping = matches.length > 1;

  return {
    awardDate,
    matches,
    governing,
    overlapping,
    note: overlapping
      ? `${awardDate} falls inside ${matches.length} overlapping ordering periods (${matches
          .map((m) => m.label)
          .join(' and ')}). The later contract governs unless the task order names an earlier one, so confirm against the task order itself.`
      : `${awardDate} falls inside one ordering period, ${governing.label}.`,
  };
}

/** Witnessing and sign-off language a compliant plan would contain. */
const WITNESSING_LANGUAGE = [
  /\bwitness(?:ed|ing|es)?\b/i,
  /\bsign[- ]?off\b/i,
  /\bcustomer (?:acceptance|approval|concurrence)\b/i,
  /\bowner(?:'|’)?s? (?:representative )?(?:acceptance|approval|witness)\b/i,
  /\bobserved by (?:the )?(?:government|agency|owner)\b/i,
];

export function findWitnessingLanguage(
  doc: SourceDocument,
): { index: number; matched: string } | null {
  for (const pattern of WITNESSING_LANGUAGE) {
    const m = pattern.exec(doc.rawText);
    if (m) return { index: m.index, matched: m[0] };
  }
  return null;
}

/**
 * Emits FM-02 only when the governing generation *requires* witnessing and the
 * plan contains no witnessing or sign-off provision at all. Under Gen 1 or Gen 2
 * the absence is reported in the receipt but is not a breach, so no flag.
 */
export function generationCheck(
  doc: SourceDocument,
  awardDate?: string,
): Flag[] {
  const date = awardDate ?? doc.metadata.awardDate;
  if (!date) {
    // Nothing is guessed. Without an award date this check cannot run, and
    // saying so is more useful than inferring a generation from a cover date.
    return [];
  }

  const lookup = lookupGeneration(date);
  const witnessing = findWitnessingLanguage(doc);
  const locator = buildLocator(doc);

  const inputs = [
    { label: 'Award date (user-supplied)', value: date },
    {
      label: 'Ordering periods matched',
      value:
        lookup.matches.length === 0
          ? 'none'
          : lookup.matches
              .map((m) => `${m.label} (${m.from} to ${m.to ?? 'open'})`)
              .join('; '),
    },
    {
      label: 'Governing generation',
      value: lookup.governing
        ? `${lookup.governing.label} — ${lookup.governing.clause}`
        : 'undetermined',
    },
    {
      label: 'Witnessing obligation',
      value: lookup.governing
        ? `"${lookup.governing.witnessing}"`
        : 'undetermined',
    },
    {
      label: 'Witnessing language in plan',
      value: witnessing ? `found — "${witnessing.matched}"` : 'none found',
    },
  ];

  const required = lookup.governing?.witnessing === 'shall';
  if (!required || witnessing) return [];

  const section = locator.sectionOf(0);

  return [
    enrichFlag({
      id: `flag-${CHECK_ID}-1`,
      failureModeId: 'FM-02',
      tier: tierOf('FM-02'),
      layer: 'deterministic',
      checkId: CHECK_ID,
      title: 'No witnessing or sign-off provision, under a generation that requires one',
      // The finding is an absence, so there is nothing to quote.
      quote: null,
      sectionRef: sectionRefOf(section),
      page: null,
      // The finding is an absence, so there is no span to highlight either.
      quoteOffsets: null,
      basis: detectionSignal('FM-02', 1),
      evidenceCaseIds: evidenceFor('FM-02'),
      receipt: {
        kind: 'lookup',
        inputs,
        computation:
          'Award date → IDIQ ordering period → witnessing obligation. Then search the plan for witnessing or sign-off language.',
        result: `${lookup.note} Under ${lookup.governing?.label} witnessing is "${lookup.governing?.witnessing}", and no witnessing schedule or sign-off provision appears anywhere in this document.`,
      },
      status: 'open',
    }),
  ];
}
